import { useEffect, useState } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import api from "../lib/api";
import CourseCard from "../components/CourseCard";
import { useSEO } from "../lib/seo";
import { ArrowRight, ShieldCheck, Star, Trophy, Sparkles } from "lucide-react";

const VENDOR_LABELS = {
  aws: { name: "AWS", title: "AWS practice tests", blurb: "Crack AWS Solutions Architect, Developer, SysOps and DevOps certifications with real-exam practice." },
  azure: { name: "Microsoft Azure", title: "Microsoft Azure practice tests", blurb: "Master AZ-104, AZ-204, AZ-305 and beyond with adaptive analytics." },
  gcp: { name: "Google Cloud", title: "Google Cloud practice tests", blurb: "Get certified on GCP — ACE, PCA, PDE — with our exam-aligned mock tests." },
  kubernetes: { name: "Kubernetes", title: "Kubernetes practice tests", blurb: "Pass CKA, CKAD and CKS with scenario-style questions and detailed explanations." },
  devops: { name: "DevOps", title: "DevOps practice tests", blurb: "From AWS DevOps Pro to Terraform Associate — practice the way you'll be tested." },
  cybersecurity: { name: "Cybersecurity", title: "Cybersecurity practice tests", blurb: "Prepare for Security+, CEH, and CISSP with real-exam mock tests." },
  cisco: { name: "Cisco", title: "Cisco practice tests", blurb: "Networking fundamentals to CCNP — sharpen with timed mocks and full explanations." },
  pmp: { name: "PMP", title: "PMP practice tests", blurb: "PMI-aligned PMP mock exams that mirror real situational questions." },
  "ai-ml": { name: "AI / ML", title: "AI & Machine Learning practice tests", blurb: "From AWS Machine Learning Specialty to Azure AI Engineer — practice with real-world scenarios." },
};

export default function LandingProgrammatic() {
  const { slug } = useParams();
  const meta = VENDOR_LABELS[slug];
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!meta) return;
    api.get(`/courses?category=${encodeURIComponent(meta.name)}&limit=24`)
      .then((r) => setCourses(r.data))
      .finally(() => setLoading(false));
  }, [meta]);

  useSEO({
    title: meta ? `Best ${meta.name} practice tests for 2026` : "Practice tests",
    description: meta?.blurb,
    jsonLd: meta && courses.length
      ? {
          "@context": "https://schema.org",
          "@type": "ItemList",
          name: meta.title,
          itemListElement: courses.slice(0, 10).map((c, i) => ({
            "@type": "ListItem",
            position: i + 1,
            url: typeof window !== "undefined" ? `${window.location.origin}/courses/${c.slug}` : "",
            name: c.title,
          })),
        }
      : undefined,
  });

  if (!meta) return <Navigate to="/marketplace" replace />;

  return (
    <div data-testid="landing-programmatic-page">
      <section className="relative border-b border-border overflow-hidden">
        <div className="absolute inset-0 hero-mesh" />
        <div className="aurora" />
        <div className="absolute inset-0 grid-bg opacity-40" />
        <div className="container-x relative py-20 lg:py-24">
          <div className="inline-flex items-center gap-2 rounded-full border border-border bg-background/60 backdrop-blur px-3 py-1 text-xs font-semibold">
            <Sparkles className="h-3.5 w-3.5 text-[color:var(--brand-blue)]" />
            Curated for 2026 exam objectives
          </div>
          <h1 className="mt-6 font-display text-4xl sm:text-5xl lg:text-6xl tracking-tighter leading-[1.05] max-w-3xl">
            Best <span className="text-gradient">{meta.name}</span> practice tests for 2026
          </h1>
          <p className="mt-4 text-lg text-muted-foreground max-w-2xl">{meta.blurb}</p>
          <div className="mt-6 flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-muted-foreground">
            <span className="flex items-center gap-1.5"><ShieldCheck className="h-4 w-4 text-[color:var(--brand-success)]" /> 30-day money-back</span>
            <span className="flex items-center gap-1.5"><Star className="h-4 w-4 text-yellow-500 fill-yellow-500" /> 4.8★ avg rating</span>
            <span className="flex items-center gap-1.5"><Trophy className="h-4 w-4 text-[color:var(--brand-gold)]" /> 184k+ students passed</span>
          </div>
        </div>
      </section>

      <section className="container-x py-16">
        <div className="flex items-end justify-between gap-4 mb-8">
          <div>
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-[color:var(--brand-blue)]">{meta.name}</div>
            <h2 className="mt-2 font-display text-3xl sm:text-4xl tracking-tight">{courses.length} practice tests available</h2>
          </div>
          <Link to="/marketplace" className="text-sm font-semibold inline-flex items-center gap-1 link-underline">
            See all certifications <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
        {loading ? (
          <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-6">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="rounded-xl border border-border h-72 animate-pulse bg-muted/40" />
            ))}
          </div>
        ) : courses.length === 0 ? (
          <div className="rounded-xl border border-dashed border-border p-16 text-center">
            <h3 className="font-display text-xl">More {meta.name} practice tests coming soon</h3>
            <p className="text-muted-foreground mt-1 text-sm">Browse the rest of the marketplace meanwhile.</p>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-6">
            {courses.map((c) => <CourseCard key={c.id} course={c} />)}
          </div>
        )}
      </section>

      <section className="container-x py-16 border-t border-border">
        <div className="max-w-3xl">
          <h2 className="font-display text-2xl sm:text-3xl tracking-tight">Why prep with BeforeExam for {meta.name}?</h2>
          <div className="mt-6 grid sm:grid-cols-3 gap-4">
            {[
              { t: "Real-exam style", d: `Questions modeled on the latest ${meta.name} exam blueprint — not free dumps.` },
              { t: "Adaptive analytics", d: "Pass-probability score and weak-topic radar after every attempt." },
              { t: "Lifetime updates", d: `Every ${meta.name} exam objective is refreshed every 30 days, free for life.` },
            ].map((b) => (
              <div key={b.t} className="rounded-xl border border-border p-5 bg-card">
                <div className="font-display text-lg">{b.t}</div>
                <div className="text-sm text-muted-foreground mt-1">{b.d}</div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
